<div class="min-h-screen p-6 bg-white dark:bg-neutral-900">
  <!-- Header -->
  <div class="flex justify-between items-center mb-6">
    <div style="visibility: hidden">hidden</div>
    <div class="flex gap-3">
      <p-button
        [label]="'jobDetailDialog.executeJob' | transloco"
        size="small"
        (onClick)="executeJob()"
        [disabled]="!validateParameters() || isExecuting"
        [loading]="isExecuting"
      >
      </p-button>
      <p-button
        [label]="'jobDetailDialog.cancel' | transloco"
        severity="secondary"
        size="small"
        (onClick)="onCancel()"
        [outlined]="true"
        [disabled]="isExecuting"
      >
      </p-button>
    </div>
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <div class="flex items-center justify-center py-12">
      <div class="text-center">
        <i class="pi pi-spin pi-spinner text-2xl text-neutral-500 dark:text-neutral-400 mb-2"></i>
        <p class="text-neutral-600 dark:text-neutral-400">
          {{ 'jobDetailDialog.loadingParameters' | transloco }}
        </p>
      </div>
    </div>
  }

  <!-- Form -->
  @if (!isLoading && parameters.length > 0) {
    <form [formGroup]="jobForm" class="space-y-6">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Left Column -->
        <div class="space-y-6">
          <!-- Job Parameters -->
          <div
            class="rounded-md border border-neutral-300 dark:border-neutral-700/30 p-6 bg-white dark:bg-neutral-800/30"
          >
            <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              {{ 'jobDetailDialog.jobParameters' | transloco }}
            </h2>

            <div class="space-y-4">
              @for (param of parameters; track param.variable) {
                <div class="mb-4">
                  <!-- Text Input -->
                  @if (param.type === 'text-input') {
                    <div>
                      <label
                        class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
                      >
                        {{ param.label }}
                        @if (param.required) {
                          <span class="text-red-700 dark:text-red-400">*</span>
                        }
                      </label>
                      <input
                        pInputText
                        [formControlName]="param.variable"
                        [placeholder]="param.label.toLowerCase()"
                        class="w-full"
                        [maxlength]="param.max_length"
                        [class.ng-invalid]="
                          jobForm.get(param.variable)?.invalid &&
                          jobForm.get(param.variable)?.touched
                        "
                      />
                      @if (param.max_length) {
                        <small class="text-neutral-500 dark:text-neutral-400 text-xs mt-1 block">
                          {{
                            'jobDetailDialog.maxCharacters' | transloco: { count: param.max_length }
                          }}
                        </small>
                      }
                      @if (
                        jobForm.get(param.variable)?.invalid && jobForm.get(param.variable)?.touched
                      ) {
                        <p-message
                          [severity]="'error'"
                          [text]="
                            'jobDetailDialog.fieldRequired' | transloco: { field: param.label }
                          "
                        >
                        </p-message>
                      }
                    </div>
                  }

                  <!-- Paragraph -->
                  @if (param.type === 'textarea') {
                    <div>
                      <label
                        class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
                      >
                        {{ param.label }}
                        @if (param.required) {
                          <span class="text-red-700 dark:text-red-400">*</span>
                        }
                      </label>
                      <textarea
                        pInputText
                        [formControlName]="param.variable"
                        [placeholder]="
                          'jobDetailDialog.enterField'
                            | transloco: { field: param.label.toLowerCase() }
                        "
                        rows="4"
                        class="w-full"
                        [maxlength]="param.max_length"
                        [class.ng-invalid]="
                          jobForm.get(param.variable)?.invalid &&
                          jobForm.get(param.variable)?.touched
                        "
                        style="resize: vertical"
                      >
                      </textarea>
                      @if (param.max_length) {
                        <small class="text-neutral-500 dark:text-neutral-400 text-xs mt-1 block">
                          {{
                            'jobDetailDialog.maxCharacters' | transloco: { count: param.max_length }
                          }}
                        </small>
                      }
                      @if (
                        jobForm.get(param.variable)?.invalid && jobForm.get(param.variable)?.touched
                      ) {
                        <p-message
                          [severity]="'error'"
                          [text]="
                            'jobDetailDialog.fieldRequired' | transloco: { field: param.label }
                          "
                        >
                        </p-message>
                      }
                    </div>
                  }

                  <!-- Select -->
                  @if (param.type === 'select' && param.options && param.options.length > 0) {
                    <div>
                      <label
                        class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
                      >
                        {{ param.label }}
                        @if (param.required) {
                          <span class="text-red-700 dark:text-red-400">*</span>
                        }
                      </label>
                      <p-select
                        [formControlName]="param.variable"
                        [options]="param.options"
                        [placeholder]="
                          'jobDetailDialog.selectField'
                            | transloco: { field: param.label.toLowerCase() }
                        "
                        class="w-full"
                      >
                      </p-select>
                      @if (
                        jobForm.get(param.variable)?.invalid && jobForm.get(param.variable)?.touched
                      ) {
                        <p-message
                          [severity]="'error'"
                          [text]="
                            'jobDetailDialog.fieldRequired' | transloco: { field: param.label }
                          "
                        >
                        </p-message>
                      }
                    </div>
                  }

                  <!-- Number Input -->
                  @if (param.type === 'number-input') {
                    <div>
                      <label
                        class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
                      >
                        {{ param.label }}
                        @if (param.required) {
                          <span class="text-red-700 dark:text-red-400">*</span>
                        }
                      </label>
                      <input
                        type="number"
                        pInputText
                        [formControlName]="param.variable"
                        [placeholder]="
                          'jobDetailDialog.enterField'
                            | transloco: { field: param.label.toLowerCase() }
                        "
                        class="w-full"
                        [class.ng-invalid]="
                          jobForm.get(param.variable)?.invalid &&
                          jobForm.get(param.variable)?.touched
                        "
                      />
                      @if (
                        jobForm.get(param.variable)?.invalid && jobForm.get(param.variable)?.touched
                      ) {
                        <p-message
                          [severity]="'error'"
                          [text]="
                            'jobDetailDialog.fieldRequired' | transloco: { field: param.label }
                          "
                        >
                        </p-message>
                      }
                    </div>
                  }

                  <!-- Checkbox -->
                  @if (param.type === 'checkbox') {
                    <div class="flex items-center gap-3">
                      <p-checkbox
                        [formControlName]="param.variable"
                        [inputId]="param.variable"
                        [binary]="true"
                      >
                      </p-checkbox>
                      <label
                        [for]="param.variable"
                        class="text-sm font-medium text-neutral-700 dark:text-neutral-300"
                      >
                        {{ param.label }}
                        @if (param.required) {
                          <span class="text-red-700 dark:text-red-400">*</span>
                        }
                      </label>
                    </div>
                  }

                  <!-- Radio Button -->
                  @if (param.type === 'radio' && param.options && param.options.length > 0) {
                    <div>
                      <label
                        class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
                      >
                        {{ param.label }}
                        @if (param.required) {
                          <span class="text-red-700 dark:text-red-400">*</span>
                        }
                      </label>
                      <div class="space-y-2">
                        @for (option of param.options; track option) {
                          <div class="flex items-center gap-3">
                            <p-radioButton
                              [formControlName]="param.variable"
                              [inputId]="param.variable + '_' + option"
                              [value]="option"
                            >
                            </p-radioButton>
                            <label
                              [for]="param.variable + '_' + option"
                              class="text-sm text-neutral-700 dark:text-neutral-300"
                            >
                              {{ option }}
                            </label>
                          </div>
                        }
                      </div>
                      @if (
                        jobForm.get(param.variable)?.invalid && jobForm.get(param.variable)?.touched
                      ) {
                        <p-message
                          [severity]="'error'"
                          [text]="
                            'jobDetailDialog.fieldRequired' | transloco: { field: param.label }
                          "
                        >
                        </p-message>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="space-y-6">
          <!-- Stream Chat -->
          <app-stream-chat [apiUrl]="apiUrl" [token]="token" [payload]="payload" />
        </div>
      </div>
    </form>
  }

  <!-- No Parameters State -->
  @if (!isLoading && parameters.length === 0) {
    <div class="flex items-center justify-center py-12">
      <div class="text-center">
        <i
          class="fa fa-light fa-exclamation-triangle text-4xl text-neutral-400 dark:text-neutral-500 mb-4"
        ></i>
        <h3 class="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
          {{ 'jobDetailDialog.noParametersFound' | transloco }}
        </h3>
        <p class="text-neutral-600 dark:text-neutral-400">
          {{ 'jobDetailDialog.noConfigurableParameters' | transloco }}
        </p>
      </div>
    </div>
  }
</div>
